// Presentation/state extracted from the pinned Countries reference; no snapshot dependency.
export type N = number | null;
export const VIEWS = [
  'Countries Margin',
  'Countries CR',
  'Product Margin',
  'Products CR',
  'Destinations Margin',
] as const;
export type View = (typeof VIEWS)[number];
export type Metric = 'sales' | 'margin' | 'estimated' | 'aov' | 'orders' | 'customers';
export type State = {
  view: View;
  period: string;
  dma: number;
  market: string;
  product: string;
  countryGroup: string;
  cancelled: string;
  buyer: string;
  language: string;
  metric: Metric;
  basis: 'BR' | 'AR';
  theme: 'light' | 'dark';
  layout: 'standard' | 'baseline';
  highlight: string;
  destination: string;
  countryPanel: string;
  productGroup: string;
  destinationGroup: string;
  from: string;
  to: string;
};
export const periods = ['Yesterday', 'Last 7 days', 'Last 14 days', 'Last 28 days', 'MTD', 'YTD'] as const;
export const sourceURLs: Record<View, string> = {
  'Countries Margin': 'CountriesTTV',
  'Countries CR': 'CountriesCR',
  'Product Margin': 'ProductTTV_1',
  'Products CR': 'ProductsCR',
  'Destinations Margin': 'DestinationsTTV',
};
export const sourceURL = (view: View) =>
  'https://10ay.online.tableau.com/t/luxuryescapescomau/views/CountriesDashboard/' +
  sourceURLs[view] +
  '?:embed=y&:showVizHome=n&:toolbar=top';
export function addDays(d: string, n: number) {
  return new Date(Date.parse(d + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);
}
export const isCR = (v: string) => v.endsWith(' CR');
export function readState(search: string, prefix = ''): State {
  const p = new URLSearchParams(search);
  const legacy: Record<string, string> = {
    countryGroup: 'country_group',
    countryPanel: 'country_panel',
    productGroup: 'product_group',
    destinationGroup: 'destination_group',
  };
  const g = (key: string, fallback: string) =>
    p.get(prefix ? prefix + key[0].toUpperCase() + key.slice(1) : key) ??
    p.get(prefix ? prefix + '_' + (legacy[key] ?? key) : key) ??
    fallback;
  const v = g('view', VIEWS[0]);
  const filter = (key: string, nativeKey = legacy[key] ?? key) => {
    const values = p.getAll(prefix ? prefix + '_' + nativeKey : nativeKey);
    return (
      p.get(prefix ? prefix + key[0].toUpperCase() + key.slice(1) : key) ?? (values.length ? values.join('|') : 'All')
    );
  };
  return {
    view: VIEWS.includes(v as View) ? (v as View) : VIEWS[0],
    period: [...periods, 'Custom'].some((period) => period === g('period', 'Last 7 days'))
      ? g('period', 'Last 7 days')
      : 'Last 7 days',
    dma: [7, 14, 21, 28, 35, 42, 49].includes(+g('dma', '7')) ? +g('dma', '7') : 7,
    market: filter('market', isCR(v) ? 'website_region' : 'market'),
    product: filter('product', isCR(v) ? 'cr_product' : 'product'),
    countryGroup: filter('countryGroup'),
    cancelled: filter('cancelled'),
    buyer: filter('buyer'),
    language: filter('language'),
    metric: ['sales', 'margin', 'estimated', 'aov', 'orders', 'customers'].includes(g('metric', 'margin'))
      ? (g('metric', 'margin') as Metric)
      : 'margin',
    basis: g('basis', 'AR') === 'BR' ? 'BR' : 'AR',
    theme: g('theme', 'light') === 'dark' ? 'dark' : 'light',
    layout: g('layout', 'standard') === 'baseline' ? 'baseline' : 'standard',
    highlight: g('highlight', 'All'),
    destination: filter('destination'),
    countryPanel: filter('countryPanel'),
    productGroup: filter('productGroup'),
    destinationGroup: filter('destinationGroup'),
    from: g('from', ''),
    to: g('to', ''),
  };
}
export function stateSearch(s: State, prefix = '', existing = '') {
  const p = new URLSearchParams(existing);
  Object.entries(s).forEach(([k, v]) => p.set(prefix ? prefix + k[0].toUpperCase() + k.slice(1) : k, String(v)));
  return p.toString();
}
export function selected(value: string, options: Array<string>) {
  return value === 'All' ? options : value === 'None' ? [] : value.split('|');
}
export const fmt = (n: N, decimals = 0) =>
  n === null
    ? 'Unknown'
    : n.toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
export const compact = (n: N) =>
  n === null ? 'Unknown' : new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
export const pct = (n: N, decimals = 1) => (n === null ? 'Unknown' : (n * 100).toFixed(decimals) + '%');
export const ratio = (a: N, b: N): N => (a === null || b === null || b === 0 ? null : a / b);
export const growth = (a: N, b: N): N => {
  const r = ratio(a, b);
  return r === null ? null : r - 1;
};
export const metricLabel = (s: Pick<State, 'metric' | 'basis'>) =>
  s.metric === 'margin'
    ? `Margin (${s.basis})`
    : s.metric === 'estimated'
      ? `Estimated Margin (${s.basis})`
      : s.metric === 'sales'
        ? `TTV (${s.basis})`
        : s.metric === 'aov'
          ? 'AoV (BR)'
          : s.metric === 'orders'
            ? 'Orders (BR)'
            : 'Customers (BR)';
export const metricOptions = [
  'Margin (BR)',
  'Margin (AR)',
  'Estimated Margin (BR)',
  'Estimated Margin (AR)',
  'Customers (BR)',
  'Orders (BR)',
];
export function metricPatch(label: string): Partial<State> {
  return {
    metric: label.startsWith('Estimated')
      ? 'estimated'
      : label.startsWith('Margin')
        ? 'margin'
        : label.startsWith('TTV')
          ? 'sales'
          : label.startsWith('AoV')
            ? 'aov'
            : label.startsWith('Orders')
              ? 'orders'
              : 'customers',
    basis: label.includes('(AR)') ? 'AR' : 'BR',
  };
}
export const countries = [
  'Australia',
  'United States',
  'New Zealand',
  'United Kingdom',
  'Singapore',
  'India',
  'Canada',
  'Germany',
  'Hong Kong',
  'Malaysia',
];
export const products = [
  'Limited Time Lux Exclusive',
  'Lux Premium Collection',
  'Lux Partner Property',
  'Limited Time Lux Partner',
  'Flights',
  'Tours',
  'Cruises',
  'Experiences',
  'Other',
];
/** The svc-reporting spec names this product_group after Tableau's group alias (also used by the share
 * legend and the product panel filter); the fixed panel list keeps Tableau's "Other" panel title. */
export const OTHER_PRODUCT_GROUP = 'Insurance, Last Minute Hotel, Service Fee';
/** Source group value that feeds a fixed panel. */
export function panelGroupKey(view: View, panel: string) {
  return view === 'Product Margin' && panel === 'Other' ? OTHER_PRODUCT_GROUP : panel;
}
/** Reset filters restores the Tableau defaults (Last 7 days, 7DMA) and clears every filter selection;
 * the view, metric and basis stay as chosen. */
export const RESET_PATCH: Partial<State> = {
  period: 'Last 7 days',
  dma: 7,
  from: '',
  to: '',
  market: 'All',
  product: 'All',
  countryGroup: 'All',
  cancelled: 'All',
  buyer: 'All',
  language: 'All',
  highlight: 'All',
  destination: 'All',
  countryPanel: 'All',
  productGroup: 'All',
  destinationGroup: 'All',
};
export const destinations = [
  'Australia',
  'Indonesia',
  'Thailand',
  'Maldives',
  'Fiji',
  'United States',
  'Vietnam',
  'Singapore',
  'EU+UK',
  'Other',
];
export const crMarkets = ['AU', 'US', 'CA', 'GB', 'DE', 'NZ', 'SG', 'HK', 'Other', 'Unknown'];
export const crProducts = [
  'Limited Time Lux Exclusive',
  'Lux Partner Property',
  'Lux Premium Collection',
  'Limited Time Lux Partner',
  'Tour',
  'Cruise',
  'Experience',
  'Rental',
  'Other',
];
export type Point = {
  date: string;
  at: number;
  current: N;
  previous: N;
  yoy: N;
  up: N;
  down: N;
  raw: N;
  rawLY: N;
  packages?: N;
  views?: N;
};
